import type { FC } from 'react';
import { Button } from 'flowbite-react';
import { HiPlus, HiTrash } from 'react-icons/hi';
import type {
  FilterCondition,
  FilterGroup,
} from '../../types/filter';
import {
  createEmptyFilterCondition,
  createEmptyFilterGroup,
  isFilterCondition,
  isFilterGroup,
} from '../../types/filter';
import { ConditionRow } from './ConditionRow';

interface ConditionGroupProps {
  group: FilterGroup;
  depth?: number;
  isFirstInParent?: boolean;
  parentConjunction?: 'and' | 'or';
  onUpdate: (updated: FilterGroup) => void;
  onDelete?: () => void;
  disabled?: boolean;
}

export const ConditionGroup: FC<ConditionGroupProps> = ({
  group,
  depth = 0,
  isFirstInParent = true,
  onUpdate,
  onDelete,
  disabled = false,
}) => {
  // Update group conjunction (and / or)
  const handleConjunctionChange = (newConjunction: 'and' | 'or') => {
    onUpdate({
      ...group,
      conjunction: newConjunction,
    });
  };

  // Add new leaf condition
  const handleAddCondition = () => {
    const newCond = createEmptyFilterCondition();
    onUpdate({
      ...group,
      children: [...group.children, newCond],
    });
  };

  // Add new nested group
  const handleAddSubgroup = () => {
    const newSubgroup = createEmptyFilterGroup(
      group.conjunction === 'and' ? 'or' : 'and',
      [createEmptyFilterCondition()]
    );
    onUpdate({
      ...group,
      children: [...group.children, newSubgroup],
    });
  };

  // Update a child node
  const handleChildUpdate = (
    index: number,
    updatedChild: FilterCondition | FilterGroup
  ) => {
    const nextChildren = [...group.children];
    nextChildren[index] = updatedChild;
    onUpdate({
      ...group,
      children: nextChildren,
    });
  };

  // Delete a child node
  const handleChildDelete = (index: number) => {
    const nextChildren = group.children.filter((_, i) => i !== index);
    onUpdate({
      ...group,
      children: nextChildren,
    });
  };

  const isNested = depth > 0;

  return (
    <div
      className={`space-y-2 ${
        isNested
          ? 'my-2 rounded-lg border border-gray-200 bg-gray-50/70 p-3 dark:border-gray-700 dark:bg-gray-800/60'
          : ''
      }`}
    >
      {/* Nested Group Header */}
      {isNested && (
        <div className="flex items-center justify-between border-b border-gray-200 pb-2 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-gray-500 uppercase dark:text-gray-400">
              {isFirstInParent ? 'Where' : group.conjunction} (Gruppe)
            </span>
            <span className="text-xs text-gray-400 dark:text-gray-500">
              Alle Bedingungen müssen erfüllt sein ({group.conjunction.toUpperCase()})
            </span>
          </div>
          {onDelete && (
            <Button
              color="gray"
              size="xs"
              onClick={onDelete}
              disabled={disabled}
              className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
              title="Gruppe entfernen"
            >
              <HiTrash className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      )}

      {/* Children list (ConditionRow or nested ConditionGroup) */}
      <div className="space-y-1">
        {group.children.map((child, index) => {
          const isFirst = index === 0;

          if (isFilterCondition(child)) {
            return (
              <ConditionRow
                key={child.id}
                condition={child}
                isFirst={isFirst}
                conjunction={group.conjunction}
                onConjunctionChange={handleConjunctionChange}
                onUpdate={(updated) => handleChildUpdate(index, updated)}
                onDelete={() => handleChildDelete(index)}
                disabled={disabled}
              />
            );
          }

          if (isFilterGroup(child)) {
            return (
              <ConditionGroup
                key={child.id}
                group={child}
                depth={depth + 1}
                isFirstInParent={isFirst}
                parentConjunction={group.conjunction}
                onUpdate={(updated) => handleChildUpdate(index, updated)}
                onDelete={() => handleChildDelete(index)}
                disabled={disabled}
              />
            );
          }

          return null;
        })}
      </div>

      {/* Add condition & Add group buttons */}
      <div className="flex flex-wrap items-center gap-2 pt-2">
        <Button
          color="light"
          size="xs"
          onClick={handleAddCondition}
          disabled={disabled}
          className="flex items-center gap-1 text-xs"
        >
          <HiPlus className="mr-1 h-3.5 w-3.5" />
          Bedingung hinzufügen
        </Button>

        {depth < 2 && (
          <Button
            color="light"
            size="xs"
            onClick={handleAddSubgroup}
            disabled={disabled}
            className="flex items-center gap-1 text-xs"
          >
            <HiPlus className="mr-1 h-3.5 w-3.5" />
            Bedingungsgruppe hinzufügen
          </Button>
        )}
      </div>
    </div>
  );
};
